import { BillingModality, BonusClassification, BonusCondition, PaymentMethod } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch, nullIfEmpty } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import {
    SupplierConditionsRequest,
    SupplierConditionsRequestBonusConditions,
} from '../api/models/supplier-conditions-request';

const getSupplierDetail = (state: AppState) => state.contactsAndOrganizations.suppliers.supplierDetail;

export const supplierConditionsSelector = createSelector(getSupplierDetail, state => state.conditions);
export const supplierDetailConditionsBonusConditionsSelector = createSelector(
    supplierConditionsSelector,
    state => state?.detailConditions?.conditions
);

export const supplierConditionsDeliveryTermsSelector = createSelector(
    supplierConditionsSelector,
    state => state.deliveryTerms
);

/**
 * The shape the mask edits in: the conditions sorted and filled out, and a
 * default for every value the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159) — compared against raw
 * server values, both the sort and every default would read as input.
 */
const withDefaults = (detailConditions?: SupplierConditionsFormValues | null): SupplierConditionsFormValues => {
    const conditions: BonusCondition[] = (detailConditions?.conditions ?? [])
        .map(condition => {
            const newCondition: BonusCondition = {
                id: condition.id,
                title: condition.title,
                mode: condition.mode,
                conditionFrom: condition.conditionFrom ?? null,
                conditionTo: condition.conditionTo ?? null,
                price: condition.price,
                currency: condition.currency,
                internalNote: condition.internalNote ?? '',
                percentage: condition.percentage,
                description: condition.description ?? '',
                unit: condition.unit,
                quantity: condition.quantity,
                supplierPayout: condition.supplierPayout,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                responsibleContactId: condition.responsibleContactId!,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                responsibleContactEmail: condition.responsibleContactEmail,
                isArchived: condition.isArchived,
            };
            return newCondition;
        })
        .sort((a, b) => {
            // New conditions should be at the top
            if (a.id <= 0) return -1;
            if (b.id <= 0) return 1;

            // Archived conditions should be at the end
            if (a.isArchived !== b.isArchived) {
                return a.isArchived ? 1 : -1;
            }

            return b.id - a.id;
        });

    const formValues: SupplierConditionsFormValues = {
        ...detailConditions,
        conditions,
        conditionsOfPurchase: detailConditions?.conditionsOfPurchase ?? '',
        bonusInternal: detailConditions?.bonusInternal ?? '',
        isChargingSupplier: detailConditions?.isChargingSupplier ?? false,
        paymentMethod: detailConditions?.paymentMethod ?? PaymentMethod.BankTransfer,
        minimumOrderValue: detailConditions?.minimumOrderValue ?? 0,
        minimumQuantitySurcharge: detailConditions?.minimumQuantitySurcharge ?? 0,
        shippingCost: detailConditions?.shippingCost ?? 0,
        shippingCostTiers: detailConditions?.shippingCostTiers ?? [],
        memberCountryCodes: detailConditions?.memberCountryCodes ?? [],
        freeShippingThreshold: detailConditions?.freeShippingThreshold ?? null,
        bonusClassificationFrom: detailConditions?.bonusClassificationFrom ?? null,
        bonusClassificationTo: detailConditions?.bonusClassificationTo ?? null,
        isVisibleInShop: detailConditions?.isVisibleInShop ?? false,
        taxRateId: detailConditions?.taxRateId ?? null,
        paymentTermId: detailConditions?.paymentTermId,
        poolPaymentTermId: detailConditions?.poolPaymentTermId ?? null,
        billingModality: detailConditions?.billingModality ?? BillingModality.Invoice,
        bonusTaxRateId: detailConditions?.bonusTaxRateId ?? null,
    };

    return formValues;
};

export const supplierDetailConditionsFormDataSelector = createSelector(getSupplierDetail, detailConditionsState => {
    const { detailConditions, errors } = detailConditionsState.conditions;

    return {
        formValues: withDefaults(detailConditions),
        errors,
        touched: errorsToTouch(errors),
    };
});

/** What the tab compares against to decide whether anything is unsaved. */
export const supplierDetailConditionsBaselineSelector = createSelector(supplierConditionsSelector, state =>
    state.baseline ? withDefaults(state.baseline) : undefined
);

export const supplierDetailConditionsRequestSelector = createSelector(getSupplierDetail, detailConditionsState => {
    const { detailConditions } = detailConditionsState.conditions;

    if (
        !detailConditions ||
        !detailConditions.deliveryTermId ||
        !detailConditions.paymentTermId ||
        !detailConditions.taxRateId ||
        !detailConditions.bonusTaxRateId
    ) {
        return null;
    }

    const conditions: SupplierConditionsRequestBonusConditions[] = detailConditions.conditions.map(condition => {
        const newCondition: SupplierConditionsRequestBonusConditions = {
            id: condition?.id && condition.id < 0 ? undefined : condition.id,
            title: condition.title,
            mode: condition.mode,
            conditionFrom: condition.conditionFrom ?? null,
            conditionTo: condition.conditionTo ?? null,
            price: condition.price,
            currency: condition.currency,
            internalNote: nullIfEmpty(condition.internalNote),
            percentage: condition.percentage,
            supplierPayout: condition.supplierPayout,
            description: nullIfEmpty(condition.description),
            unit: nullIfEmpty(condition.unit),
            quantity: condition.quantity,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            responsibleContactId: condition.responsibleContactId!,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            responsibleContactEmail: condition.responsibleContactEmail!,
            isArchived: condition.isArchived,
        };

        return newCondition;
    });

    const request: SupplierConditionsRequest = {
        deliveryTermId: detailConditions.deliveryTermId,
        paymentMethod: detailConditions.paymentMethod,
        conditionsOfPurchase: nullIfEmpty(detailConditions.conditionsOfPurchase),
        freeShippingThreshold: detailConditions.freeShippingThreshold ?? null,
        minimumQuantitySurcharge: detailConditions.minimumQuantitySurcharge ?? 0,
        isChargingSupplier: detailConditions.isChargingSupplier ?? false,
        bonusInternal: nullIfEmpty(detailConditions.bonusInternal),
        minimumOrderValue: detailConditions.minimumOrderValue ?? 0,
        shippingCost: detailConditions.shippingCost ?? 0,
        // A step without a value is a row still being typed, not a rule.
        shippingCostTiers: (detailConditions.shippingCostTiers ?? [])
            .filter(tier => `${tier.fromNetValue ?? ''}` !== '' && `${tier.shippingCost ?? ''}` !== '')
            .map(tier => ({ fromNetValue: Number(tier.fromNetValue), shippingCost: Number(tier.shippingCost) })),
        memberCountryCodes: detailConditions.memberCountryCodes ?? [],
        bonusClassification: nullIfEmpty(detailConditions.bonusClassification),
        bonusClassificationFrom: detailConditions.bonusClassificationFrom ?? null,
        bonusClassificationTo: detailConditions.bonusClassificationTo ?? null,
        isVisibleInShop:
            detailConditions.bonusClassification === BonusClassification.Contract || detailConditions.isVisibleInShop,
        conditions,
        taxRateId: detailConditions.taxRateId,
        paymentTermId: detailConditions.paymentTermId,
        poolPaymentTermId: detailConditions.poolPaymentTermId ?? null,
        billingModality: detailConditions.billingModality ?? BillingModality.Invoice,
        bonusTaxRateId: detailConditions.bonusTaxRateId,
    };

    return request;
});
